"use client"

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Award, Target, MapPin, MessageSquare, ChevronDown, ChevronUp, Share2, Download, Info, Users, Compass } from 'lucide-react';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { useMediaQuery } from '@/hooks/use-mobile';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export default function GoldenGooseResult({ analysisData }) {
  const [expandedPartner, setExpandedPartner] = useState(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const resultRef = React.useRef(null);
  
  if (!analysisData) return null;

  const { 
    targetAssessment, 
    goldenGoosePartners, 
    networkingStrategy, 
    networkingLocations, 
    visualElements, 
    analysis, 
    isGoldenGoose 
  } = analysisData;
  
  // Format key metrics labels for the chart
  const chartLabels = Object.keys(targetAssessment.keyMetrics).map(key => 
    key.replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + 
    key.replace(/([A-Z])/g, ' $1').trim().slice(1)
  );

  // Prepare chart data
  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Assessment',
        data: Object.values(targetAssessment.keyMetrics),
        backgroundColor: 'rgba(255, 215, 0, 0.2)',
        borderColor: 'rgba(255, 215, 0, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(255, 215, 0, 1)',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(255, 215, 0, 1)',
      },
    ],
  };

  // Chart options
  const chartOptions = {
    scales: {
      r: {
        angleLines: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          display: false,
          stepSize: 20,
        },
        pointLabels: {
          font: {
            size: isMobile ? 8 : 12,
          },
          color: 'rgba(0, 0, 0, 0.7)',
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
        },
        padding: 12,
        displayColors: false,
        callbacks: {
          title: (items) => {
            return items[0].label;
          },
          label: (context) => {
            return `Score: ${context.raw}%`;
          }
        }
      }
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6
      },
      line: {
        tension: 0.2
      }
    },
    responsive: true,
    maintainAspectRatio: true,
  };

  const togglePartner = (index) => {
    if (expandedPartner === index) {
      setExpandedPartner(null);
    } else {
      setExpandedPartner(index);
    }
  };

  // Calculate the highest metric
  const highestMetric = Object.entries(targetAssessment.keyMetrics).reduce(
    (highest, [key, value]) => 
      value > highest.value ? { key, value } : highest, 
    { key: '', value: 0 }
  );

  // Calculate the lowest metric
  const lowestMetric = Object.entries(targetAssessment.keyMetrics).reduce(
    (lowest, [key, value]) => 
      value < lowest.value ? { key, value } : lowest, 
    { key: '', value: 100 }
  );

  // Export function for downloading as PDF
  const handleExport = async () => {
    if (!resultRef.current) return;

    try {
      const element = resultRef.current;
      const canvas = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      
      pdf.addImage(imgData, 'PNG', imgX, 0, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`golden-goose-analysis-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export as PDF. Please try again.');
    }
  };

  // Share function
  const handleShare = async () => {
    try {
      // Create a simplified object for sharing
      const shareData = {
        title: visualElements.displayTitle,
        text: `Golden Goose Analysis: ${visualElements.displayTitle}\n\nScore: ${targetAssessment.score}%\n\n${analysis.substring(0, 100)}...`,
        // We could add a URL here if this was deployed to a shareable location
      };
      
      // Use the Web Share API if available
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        const shareText = `${shareData.title}\n\n${shareData.text}`;
        await navigator.clipboard.writeText(shareText);
        alert('Analysis summary copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Unable to share. Please try again.');
    }
  };

  return (
    <div className="golden-goose-result max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" ref={resultRef}>
      {/* Header with animated gradient border */}
      <div className="relative rounded-xl overflow-hidden bg-white shadow-lg p-6 border border-gray-200">
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 flex items-center">
                {isGoldenGoose && <Award className="h-8 w-8 mr-2 text-gray-600" />}
                {visualElements.displayTitle}
              </h1>
              <p className="text-gray-600 italic">{visualElements.visualMetaphor}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Badge variant={isGoldenGoose ? "success" : "destructive"} className="text-sm py-1 px-3 flex items-center justify-center">
                {isGoldenGoose ? (
                  <><Award className="w-4 h-4 mr-1" /> True Golden Goose</>
                ) : (
                  <>Not a Golden Goose</>
                )}
              </Badge>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-1 bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
                  onClick={handleShare}
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-1 bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
                  onClick={handleExport}
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content with tabs */}
      <Tabs defaultValue="partners" className="w-full">
        <TabsList className="grid grid-cols-3 mb-6 bg-white border border-gray-200 p-1 rounded-md">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 bg-white text-gray-700 hover:bg-gray-50 rounded-sm"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="partners" 
            className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 bg-white text-gray-700 hover:bg-gray-50 rounded-sm"
          >
            Partners
          </TabsTrigger>
          <TabsTrigger 
            value="strategy" 
            className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 data-[state=active]:border-b-2 data-[state=active]:border-amber-500 bg-white text-gray-700 hover:bg-gray-50 rounded-sm"
          >
            Strategy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Left column - Chart and Analysis */}
            <div className="md:col-span-8 space-y-6">
              {/* Chart Card */}
              <Card className="overflow-hidden border-0 shadow-md">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Target className="h-5 w-5 mr-2 text-gray-600" />
                      <CardTitle>Golden Goose Assessment</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-gray-700 border-gray-200 bg-white">
                      Score: {targetAssessment.score}%
                    </Badge>
                  </div>
                  <CardDescription>Radar chart showing key metrics assessment</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 pb-2">
                  <div className="aspect-square w-full max-w-md mx-auto">
                    <Radar data={chartData} options={chartOptions} />
                  </div>
                </CardContent>
              </Card>

              {/* Analysis Card */}
              <Card className="border-0 shadow-md">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                  <div className="flex items-center">
                    <Info className="h-5 w-5 mr-2 text-gray-600" />
                    <CardTitle>Detailed Analysis</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">{analysis}</p>
                </CardContent>
              </Card>
            </div>

            {/* Right column - Metrics and Insights */}
            <div className="md:col-span-4 space-y-6">
              {/* Score Card */}
              <Card className="border-0 shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-gray-700 to-gray-600 p-6 text-white">
                  <h3 className="text-xl font-semibold mb-1">Golden Goose Score</h3>
                  <div className="text-5xl font-bold">{targetAssessment.score}%</div>
                </div>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Strongest: {highestMetric.key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-sm font-semibold text-green-600">{highestMetric.value}%</span>
                      </div>
                      <Progress value={highestMetric.value} className="h-2 bg-gray-100" indicatorClassName="bg-green-500" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">Weakest: {lowestMetric.key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="text-sm font-semibold text-red-600">{lowestMetric.value}%</span>
                      </div>
                      <Progress value={lowestMetric.value} className="h-2 bg-gray-100" indicatorClassName="bg-red-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Strategy Highlights */}
              <Card className="border-0 shadow-md">
                <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                  <div className="flex items-center">
                    <Compass className="h-5 w-5 mr-2 text-gray-600" />
                    <CardTitle>Strategy Highlights</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-sm mb-1 text-amber-700">Networking Strategy</h4>
                      <p className="text-sm text-gray-600">{networkingStrategy}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm mb-1 text-amber-700">Recommended Networking Locations</h4>
                      <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                        {networkingLocations.map((location, index) => (
                          <li key={index}>{location}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="partners" className="mt-0">
          {/* Golden Goose Partners Table Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-gray-600" />
                <CardTitle>Golden Goose Partners</CardTitle>
              </div>
              <CardDescription>
                These 20 potential Golden Goose partners can provide ongoing referrals
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {/* Desktop view: Full table */}
              <div className="hidden md:block">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b border-gray-200" style={{ width: '25%' }}>Golden Goose</th>
                        <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b border-gray-200" style={{ width: '40%' }}>Why They Want To Meet You</th>
                        <th className="py-3 px-4 text-left font-semibold text-gray-700 border-b border-gray-200" style={{ width: '35%' }}>5 Referral Partners They Want</th>
                      </tr>
                    </thead>
                    <tbody>
                      {goldenGoosePartners.map((partner, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/30 hover:bg-gray-50/50'}>
                          <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-800">{partner.title}</td>
                          <td className="py-3 px-4 border-b border-gray-100 break-words text-gray-700">{partner.whyTheyWantToMeet}</td>
                          <td className="py-3 px-4 border-b border-gray-100">
                            <ul className="list-disc list-inside">
                              {partner.referralPartnersTheyWant.map((referral, idx) => (
                                <li key={idx} className="text-sm mb-1 break-words text-gray-700">{referral}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile view: Accordion cards */}
              <div className="md:hidden">
                {goldenGoosePartners.map((partner, index) => (
                  <div key={index} className="border-b border-gray-200 last:border-b-0">
                    <button
                      onClick={() => togglePartner(index)}
                      className="w-full py-4 px-4 flex justify-between items-center hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="text-left truncate flex-1">
                        <h3 className="font-medium text-gray-800 truncate">{partner.title}</h3>
                      </div>
                      {expandedPartner === index ? (
                        <ChevronUp className="h-5 w-5 text-gray-600 flex-shrink-0 ml-2" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-600 flex-shrink-0 ml-2" />
                      )}
                    </button>
                    
                    {expandedPartner === index && (
                      <div className="p-4 pt-0 bg-gray-50/20">
                        <div className="mt-2">
                          <h4 className="text-sm font-medium text-gray-800 mb-1">Why They Want To Meet You:</h4>
                          <p className="text-sm text-gray-700 mb-3 break-words">{partner.whyTheyWantToMeet}</p>
                          
                          <h4 className="text-sm font-medium text-gray-800 mb-1">Referral Partners They Want:</h4>
                          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                            {partner.referralPartnersTheyWant.map((referral, idx) => (
                              <li key={idx} className="break-words">{referral}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="strategy" className="mt-0">
          {/* Strategy Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <div className="flex items-center">
                <Compass className="h-5 w-5 mr-2 text-gray-600" />
                <CardTitle>Networking Strategy</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-700 mb-6 whitespace-pre-line leading-relaxed">{networkingStrategy}</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Recommended Networking Locations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {networkingLocations.map((location, index) => (
                  <div key={index} className="bg-gray-50/50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">{location}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Fixed share/export buttons for mobile */}
      <div className="md:hidden fixed bottom-4 right-4 flex flex-col gap-2 z-50">
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full shadow-lg bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
          onClick={handleShare}
        >
          <Share2 className="h-5 w-5" />
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="rounded-full shadow-lg bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
          onClick={handleExport}
        >
          <Download className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
